/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from 'react';
import { createFlow, createContact } from '../../../../api/remiApi';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUser } from '../../../../hooks/useUser';
import { generateLeadEmail } from '../../../../utils/generateLeadEmail';
import { useUpdateOrganizationSettings } from '../../../../hooks/organizations/useUpdateOrganizationSettings';
import { generateVoicemail as generateVoicemailAPI } from '../../../../api/flows';
import moment from 'moment-timezone';

interface AutoCreateResult {
  success: boolean;
  flowsCreated: string[];
  testContactCreated: boolean;
  errors: string[];
}

interface ProgressState {
  percentage: number;
  currentStep: string;
}

// Total steps: 2 Convert + 4 Engage + 1 Revive + 1 Test Contact = 8
const TOTAL_STEPS = 8;

export const useAutoCreateFlows = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState<ProgressState>({ percentage: 0, currentStep: '' });
  const { fetchOrganization } = useOrganizationStore();
  const { user } = useUser();
  const { updateSettings } = useUpdateOrganizationSettings();

  const updateProgress = useCallback((step: number, stepName: string) => {
    const percentage = Math.round((step / TOTAL_STEPS) * 100);
    setProgress({ percentage, currentStep: stepName });
  }, []);

  // Check if flows have already been auto-created (reads fresh state from store)
  const areFlowsAlreadyCreated = useCallback(() => {
    const currentOrg = useOrganizationStore.getState().organization;
    return Boolean((currentOrg as any)?.onboardingSetup?.flows_auto_created);
  }, []);

  const createAllFlows = async (options?: { skipFlagCheck?: boolean }): Promise<AutoCreateResult> => {
    // Always read fresh org from store (not stale React closure)
    const currentOrg = useOrganizationStore.getState().organization;
    if (!currentOrg?.id || !currentOrg?.org_name) {
      throw new Error('Organization not found');
    }

    // Safety check: Don't create flows if already created (unless explicitly skipped)
    if (!options?.skipFlagCheck && areFlowsAlreadyCreated()) {
      console.log('Flows already created, skipping auto-creation');
      return {
        success: true,
        flowsCreated: [],
        testContactCreated: false,
        errors: [],
      };
    }

    setIsCreating(true);
    setProgress({ percentage: 0, currentStep: 'Starting...' });
    let currentStep = 0;
    const orgId = currentOrg.id;
    const orgName = currentOrg.org_name;
    const timezone = currentOrg.timezone || 'America/New_York';
    const results: AutoCreateResult = {
      success: false,
      flowsCreated: [],
      testContactCreated: false,
      errors: [],
    };

    try {
      // Default schedule settings for all flows
      const defaultAllowedDays = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      const defaultAllowedHours = { start_hour: 9, end_hour: 18 };

      // Current date for Revive flow
      const today = moment().format('YYYY-MM-DD');

      // Get current year for Engage flows
      const currentYear = moment().year();

      // ==================
      // CONVERT FLOWS (2)
      // ==================
      const convertFlows = [
        {
          name: `${orgName} - Buyer`,
          description:
            'We are calling people who just requested to see more properties on our website.',
          lead_source: 'speculo_buyer',
        },
        {
          name: `${orgName} - Seller`,
          description: 'We are calling people who just requested a home valuation on our website.',
          lead_source: 'speculo_seller',
        },
      ];

      for (const flow of convertFlows) {
        currentStep++;
        updateProgress(currentStep, `Creating Convert flow: ${flow.description}...`);
        try {
          const leadEmail = generateLeadEmail(orgId, flow.name);

          // Generate voicemail content first so we can include it in the
          // create payload (avoids a follow-up PUT that nulls other fields).
          let voicemailContent: string | undefined;
          try {
            const vmResponse = await generateVoicemailAPI(flow.description);
            voicemailContent = vmResponse.voicemail;
          } catch (vmError) {
            console.warn(`Voicemail generation failed for ${flow.name}:`, vmError);
          }

          await createFlow(orgId, {
            name: flow.name,
            description: flow.description,
            description_id: '',
            flow_type: 'Convert',
            sub_type: '',
            timezone,
            country_code: 'US',
            transfer_numbers: {},
            lead_email: leadEmail,
            lead_source: flow.lead_source,
            use_round_robin: false,
            ...(voicemailContent ? { voicemail_content: voicemailContent } : {}),
          });
          results.flowsCreated.push(flow.name);
        } catch (error) {
          results.errors.push(`Failed to create ${flow.name}: ${error}`);
        }
      }

      // ==================
      // ENGAGE FLOWS (4)
      // ==================
      const engageFlows = [
        {
          name: `${orgName} - Engage Q1 ${currentYear}: Vendor Connection`,
          description: 'We are offering a trusted vendor list to our favorite clients.',
          startDate: `${currentYear}-01-01`,
          schedulingWindow: 90,
        },
        {
          name: `${orgName} - Engage Q2 ${currentYear}: Home Value Report`,
          description: 'We are offering home valuations to our favorite clients.',
          startDate: `${currentYear}-04-01`,
          schedulingWindow: 91,
        },
        {
          name: `${orgName} - Engage Q3 ${currentYear}: Home Improvement`,
          description:
            'We are offering support on potential home improvement goals to our favorite clients.',
          startDate: `${currentYear}-07-01`,
          schedulingWindow: 92,
        },
        {
          name: `${orgName} - Engage Q4 ${currentYear}: Custom Market Report`,
          description: 'We are offering market reports to our favorite clients.',
          startDate: `${currentYear}-10-01`,
          schedulingWindow: 92,
        },
      ];

      for (const flow of engageFlows) {
        currentStep++;
        updateProgress(currentStep, `Creating Engage flow: ${flow.description}...`);
        try {
          const scheduledFor = moment
            .tz(`${flow.startDate} 09:00`, 'YYYY-MM-DD HH:mm', timezone)
            .format();

          // Generate voicemail content before create.
          let voicemailContent: string | undefined;
          try {
            const vmResponse = await generateVoicemailAPI(flow.description);
            voicemailContent = vmResponse.voicemail;
          } catch (vmError) {
            console.warn(`Voicemail generation failed for ${flow.name}:`, vmError);
          }

          await createFlow(orgId, {
            name: flow.name,
            description: flow.description,
            description_id: '',
            flow_type: 'Engage',
            sub_type: 'quarter',
            timezone,
            country_code: 'US',
            transfer_numbers: {},
            scheduled_for: scheduledFor,
            scheduling_window: flow.schedulingWindow,
            schedule_over_unit: 'days',
            allowed_days: defaultAllowedDays,
            allowed_hours: defaultAllowedHours,
            exclude_holidays: true,
            use_round_robin: false,
            status: 'draft',
            ...(voicemailContent ? { voicemail_content: voicemailContent } : {}),
          });
          results.flowsCreated.push(flow.name);
        } catch (error) {
          results.errors.push(`Failed to create ${flow.name}: ${error}`);
        }
      }

      // ==================
      // REVIVE FLOW (1)
      // ==================
      currentStep++;
      updateProgress(currentStep, 'Creating Revive flow...');
      try {
        const reviveFlowName = `${orgName} - Basic Revive Test`;
        const reviveScheduledFor = moment
          .tz(`${today} 09:00`, 'YYYY-MM-DD HH:mm', timezone)
          .format();

        const reviveDescription =
          'You are calling people who connected with us in the past about potential real estate needs and now just checking in.';

        // Generate voicemail content before create.
        let reviveVoicemailContent: string | undefined;
        try {
          const vmResponse = await generateVoicemailAPI(reviveDescription);
          reviveVoicemailContent = vmResponse.voicemail;
        } catch (vmError) {
          console.warn('Voicemail generation failed for Revive flow:', vmError);
        }

        await createFlow(orgId, {
          name: reviveFlowName,
          description: reviveDescription,
          description_id: '',
          flow_type: 'Revive',
          sub_type: '',
          timezone,
          country_code: 'US',
          transfer_numbers: {},
          scheduled_for: reviveScheduledFor,
          scheduling_window: 365,
          schedule_over_unit: 'days',
          allowed_days: defaultAllowedDays,
          allowed_hours: defaultAllowedHours,
          exclude_holidays: true,
          use_round_robin: false,
          status: 'draft',
          ...(reviveVoicemailContent ? { voicemail_content: reviveVoicemailContent } : {}),
        });
        results.flowsCreated.push(reviveFlowName);
      } catch (error) {
        results.errors.push(`Failed to create Revive flow: ${error}`);
      }

      // ==================
      // TEST CONTACT
      // ==================
      currentStep++;
      updateProgress(currentStep, 'Creating test contact...');
      if (user?.phoneNumber && user?.firstName) {
        try {
          const now = new Date().toISOString();
          await createContact({
            organization_id: orgId,
            firstName: user.firstName,
            lastName: user.lastName || '',
            phoneNumber: user.phoneNumber,
            email: user.email || '',
            opt_in: true,
            opt_in_date: now.split('T')[0],
            opt_in_desc: 'Speculo onboarding - test contact',
            call_tester: true,
            tags: ['test-contact'],
          });
          results.testContactCreated = true;
        } catch (error) {
          results.errors.push(`Failed to create test contact: ${error}`);
        }
      }

      results.success = results.flowsCreated.length > 0;

      // Expected: 2 Convert + 4 Engage + 1 Revive = 7 flows
      const EXPECTED_FLOWS_COUNT = 7;
      const allFlowsCreated = results.flowsCreated.length >= EXPECTED_FLOWS_COUNT;
      const noErrors = results.errors.length === 0;

      // Only set the safety flag if ALL flows were created successfully
      // This ensures partial failures (e.g., internet drop) will retry on next attempt
      if (allFlowsCreated && noErrors && currentOrg?.id) {
        try {
          await updateSettings({
            id: currentOrg.id,
            data: {
              onboardingSetup: {
                ...(useOrganizationStore.getState().organization?.onboardingSetup ?? {}),
                flows_auto_created: true,
                flows_auto_created_at: new Date().toISOString(),
                flows_created_count: results.flowsCreated.length,
              },
            } as any,
          });
          // Refresh organization to get updated flag
          await fetchOrganization(true);
        } catch (flagError) {
          console.error('Error setting flowsAutoCreated flag:', flagError);
          // Don't fail the whole operation if flag setting fails
        }
      } else if (results.flowsCreated.length > 0) {
        // Partial success - log for debugging but don't set flag
        console.warn(
          `Partial flow creation: ${results.flowsCreated.length}/${EXPECTED_FLOWS_COUNT} flows created, ${results.errors.length} errors`
        );
      }

      return results;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createAllFlows,
    areFlowsAlreadyCreated,
    isCreating,
    progress,
  };
};
